(() => {
  "use strict";

  const TOKEN_KEY = "customerToken";
  const REFRESH_TOKEN_KEY = "customerRefreshToken";
  const REFRESH_EXPIRES_AT_KEY = "customerRefreshExpiresAt";
  const EXPIRES_IN_KEY = "customerExpiresIn";
  const LEGACY_SESSION_KEY = "peguu:customer-auth";
  const API_BASE_URL =
    window.PeguuApi?.endpoint?.("customers") || "/api/customers";

  let refreshPromise = null;
  let session = null;

  const dispatchAuthState = (authenticated) => {
    window.dispatchEvent(
      new CustomEvent("peguu:auth-state-changed", {
        detail: {
          authenticated,
          session: authenticated ? getSession() : null,
        },
      })
    );
  };

  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch (error) {
      return null;
    }
  };

  const createApiError = (response, payload, fallback) => {
    const error = new Error(payload?.message || fallback);
    error.status = response.status;
    error.payload = payload;
    return error;
  };

  const readLegacySession = () => {
    try {
      const stored = localStorage.getItem(LEGACY_SESSION_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      return null;
    }
  };

  const readStoredCustomer = () => {
    const current = session?.customer;
    if (current) return current;

    return readLegacySession()?.customer || null;
  };

  const getToken = () => {
    try {
      return String(localStorage.getItem(TOKEN_KEY) || "").trim();
    } catch (error) {
      return "";
    }
  };

  const getRefreshToken = () => {
    try {
      return String(localStorage.getItem(REFRESH_TOKEN_KEY) || "").trim();
    } catch (error) {
      return "";
    }
  };

  const isRefreshExpired = () => {
    try {
      const value = localStorage.getItem(REFRESH_EXPIRES_AT_KEY);
      if (!value) return false;

      const timestamp = Number(value);
      const expiresAt = Number.isFinite(timestamp)
        ? timestamp < 100000000000
          ? timestamp * 1000
          : timestamp
        : Date.parse(value);

      return Number.isFinite(expiresAt) && Date.now() >= expiresAt;
    } catch (error) {
      return false;
    }
  };

  const saveSession = (authData = {}) => {
    const token = String(authData.token || "").trim();
    const refreshToken = String(authData.refreshToken || "").trim();
    const customer = authData.customer || session?.customer || null;

    if (!token) {
      throw new Error("La respuesta de autenticación no contiene token.");
    }

    try {
      localStorage.setItem(TOKEN_KEY, token);

      if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      }

      if (authData.refreshExpiresAt) {
        localStorage.setItem(
          REFRESH_EXPIRES_AT_KEY,
          String(authData.refreshExpiresAt)
        );
      }

      if (authData.expiresIn !== undefined) {
        localStorage.setItem(EXPIRES_IN_KEY, String(authData.expiresIn));
      }

      const nextSession = {
        token,
        refreshToken: refreshToken || getRefreshToken(),
        refreshExpiresAt:
          authData.refreshExpiresAt ||
          localStorage.getItem(REFRESH_EXPIRES_AT_KEY) ||
          null,
        expiresIn:
          authData.expiresIn ??
          localStorage.getItem(EXPIRES_IN_KEY) ??
          null,
        customer,
        savedAt: new Date().toISOString(),
      };

      session = nextSession;
      localStorage.setItem(LEGACY_SESSION_KEY, JSON.stringify(nextSession));

      window.dispatchEvent(
        new CustomEvent("peguu:customer-authenticated", {
          detail: nextSession,
        })
      );
      dispatchAuthState(true);

      return nextSession;
    } catch (error) {
      throw new Error("No fue posible guardar la sesión del cliente.");
    }
  };

  const migrateLegacySession = () => {
    const existingToken = getToken();
    if (existingToken) return;

    const legacy = readLegacySession();
    if (!legacy?.token) return;

    try {
      localStorage.setItem(TOKEN_KEY, String(legacy.token));
      if (legacy.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, String(legacy.refreshToken));
      }
      if (legacy.refreshExpiresAt) {
        localStorage.setItem(
          REFRESH_EXPIRES_AT_KEY,
          String(legacy.refreshExpiresAt)
        );
      }
    } catch (error) {
      console.warn("Peguu Auth: no fue posible migrar la sesión anterior.");
    }
  };

  const getSession = () => {
    migrateLegacySession();
    const token = getToken();
    if (!token) return null;

    if (!session || session.token !== token) {
      const legacy = readLegacySession() || {};
      session = {
        ...legacy,
        token,
        refreshToken: getRefreshToken(),
        refreshExpiresAt:
          localStorage.getItem(REFRESH_EXPIRES_AT_KEY) || null,
        expiresIn: localStorage.getItem(EXPIRES_IN_KEY) || null,
        customer: readStoredCustomer(),
      };
    }

    return session;
  };

  const clearSession = () => {
    [
      TOKEN_KEY,
      REFRESH_TOKEN_KEY,
      REFRESH_EXPIRES_AT_KEY,
      EXPIRES_IN_KEY,
      LEGACY_SESSION_KEY,
    ].forEach((key) => localStorage.removeItem(key));

    session = null;
    window.dispatchEvent(new CustomEvent("peguu:customer-logged-out"));
    dispatchAuthState(false);
  };

  const refreshSession = () => {
    if (refreshPromise) return refreshPromise;

    const refreshToken = getRefreshToken();
    if (!refreshToken || isRefreshExpired()) {
      clearSession();
      return Promise.reject(
        Object.assign(new Error("La sesión del cliente expiró."), {
          status: 401,
        })
      );
    }

    refreshPromise = (async () => {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ refreshToken }),
      });
      const payload = await parseJson(response);

      if (!response.ok) {
        if (response.status === 400 || response.status === 401) {
          clearSession();
        }
        throw createApiError(
          response,
          payload,
          "No fue posible renovar la sesión."
        );
      }

      return saveSession(payload?.data || payload || {});
    })().finally(() => {
      refreshPromise = null;
    });

    return refreshPromise;
  };

  const authFetch = async (input, options = {}) => {
    const createRequest = () => {
      const headers = new Headers(options.headers || {});
      const token = getToken();

      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }

      return new Request(input, {
        ...options,
        headers,
      });
    };

    const response = await fetch(createRequest());
    if (response.status !== 401) return response;

    if (!getRefreshToken()) {
      clearSession();
      return response;
    }

    await refreshSession();
    return fetch(createRequest());
  };

  const verifySession = async () => {
    const current = getSession();
    if (!current?.token) return null;

    if (isRefreshExpired()) {
      clearSession();
      return null;
    }

    const verify = async () => {
      const response = await fetch(`${API_BASE_URL}/auth/verify`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (!response.ok) {
        const payload = await parseJson(response);
        throw createApiError(
          response,
          payload,
          "No fue posible validar la sesión."
        );
      }

      const payload = await parseJson(response);
      const verifiedCustomer = payload?.data?.customer || payload?.data;

      return saveSession({
        ...getSession(),
        customer: verifiedCustomer || getSession()?.customer,
      });
    };

    try {
      return await verify();
    } catch (error) {
      if (error?.status !== 401) {
        return null;
      }

      try {
        await refreshSession();
        return await verify();
      } catch (refreshError) {
        if (
          refreshError?.status === 400 ||
          refreshError?.status === 401 ||
          !getRefreshToken() ||
          isRefreshExpired()
        ) {
          clearSession();
        }
        return null;
      }
    }
  };

  window.PeguuAuthClient = Object.freeze({
    authFetch,
    clearSession,
    getCustomer: () => getSession()?.customer || null,
    getSession,
    getToken,
    isAuthenticated: () => Boolean(getToken()),
    isEmailVerified: () => getSession()?.customer?.emailVerified === true,
    logout: async () => {
      const token = getToken();
      const refreshToken = getRefreshToken();

      try {
        await window.PeguuCart
          ?.preserveCustomerCartAsGuest?.();
      } catch (error) {
        console.warn(
          "Peguu Auth: no fue posible conservar localmente el carrito antes de cerrar sesión.",
          error
        );
      }

      try {
        if (token && refreshToken) {
          await fetch(`${API_BASE_URL}/logout`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ refreshToken }),
          });
        }
      } catch (error) {
        console.warn(
          "Peguu Auth: no fue posible invalidar la sesión en el servidor.",
          error
        );
      } finally {
        clearSession();
      }
    },
    refresh: refreshSession,
    saveSession,
    verify: verifySession,
  });

  migrateLegacySession();
  if (getToken()) {
    window.setTimeout(() => {
      verifySession().catch(() => {});
    }, 0);
  }
})();
